import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const AdherenceStatsPanel = ({ stats }) => {
  const { weeklyAssigned = 0, weeklyCompleted = 0, monthlyAssigned = 0, monthlyCompleted = 0, currentStreak = 0 } = stats || {};

  const weeklyRate = weeklyAssigned > 0 ? Math.round((weeklyCompleted / weeklyAssigned) * 100) : 0;
  const monthlyRate = monthlyAssigned > 0 ? Math.round((monthlyCompleted / monthlyAssigned) * 100) : 0;

  const getRateColor = (rate) => {
    if (rate >= 80) return '#AAFF00';
    if (rate >= 60) return '#FF6B00';
    return '#ff4444';
  };

  const GlowBar = ({ label, completed, assigned, rate }) => (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{label}</span>
        <span className="text-xs font-bold" style={{ color: getRateColor(rate), fontFamily: "'Inter Tight', 'Inter', sans-serif", textShadow: `0 0 8px ${getRateColor(rate)}88` }}>{rate}%</span>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${rate}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={{
            background: getRateColor(rate),
            boxShadow: `0 0 8px ${getRateColor(rate)}88`,
          }}
        />
      </div>
      <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>{completed}/{assigned} workouts</p>
    </div>
  );

  return (
    <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)' }}>
          <Icon name="TrendingUp" size={16} style={{ color: '#00D4FF' }} />
        </div>
        <h3 className="text-base font-bold" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Adherence</h3>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <p className="text-2xl font-extrabold" style={{ color: getRateColor(weeklyRate), fontFamily: "'Inter Tight', 'Inter', sans-serif", textShadow: `0 0 10px ${getRateColor(weeklyRate)}66` }}>{weeklyRate}%</p>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>This Week</p>
        </div>
        <div className="text-center p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <p className="text-2xl font-extrabold" style={{ color: getRateColor(monthlyRate), fontFamily: "'Inter Tight', 'Inter', sans-serif", textShadow: `0 0 10px ${getRateColor(monthlyRate)}66` }}>{monthlyRate}%</p>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>This Month</p>
        </div>
        <div className="text-center p-3 rounded-xl" style={{ background: 'rgba(255,107,0,0.08)', border: '1px solid rgba(255,107,0,0.2)' }}>
          <div className="flex items-center justify-center gap-1">
            <p className="text-2xl font-extrabold" style={{ color: '#FF6B00', fontFamily: "'Inter Tight', 'Inter', sans-serif", textShadow: '0 0 10px #FF6B0066' }}>{currentStreak}</p>
            <span className="text-base">🔥</span>
          </div>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>Day Streak</p>
        </div>
      </div>

      <div className="space-y-3">
        <GlowBar label="This Week" completed={weeklyCompleted} assigned={weeklyAssigned} rate={weeklyRate} />
        <GlowBar label="This Month" completed={monthlyCompleted} assigned={monthlyAssigned} rate={monthlyRate} />
      </div>

      {weeklyRate >= 80 && (
        <div className="mt-4 flex items-center gap-2 p-3 rounded-xl" style={{ background: 'rgba(170,255,0,0.06)', border: '1px solid rgba(170,255,0,0.2)' }}>
          <Icon name="Award" size={16} className="flex-shrink-0" style={{ color: '#AAFF00' }} />
          <p className="text-xs font-medium" style={{ color: '#AAFF00' }}>Great consistency! You completed {weeklyRate}% of your workouts this week.</p>
        </div>
      )}
      {weeklyRate < 60 && weeklyAssigned > 0 && (
        <div className="mt-4 flex items-center gap-2 p-3 rounded-xl" style={{ background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.2)' }}>
          <Icon name="AlertTriangle" size={16} className="flex-shrink-0" style={{ color: '#FF6B00' }} />
          <p className="text-xs font-medium" style={{ color: '#FF6B00' }}>You completed {weeklyRate}% of your scheduled workouts last week. Keep going!</p>
        </div>
      )}
    </div>
  );
};

export default AdherenceStatsPanel;
